import { useLabelVersion } from '../../features/labels/LabelsProvider';
import { labelCentro, labelRegion, labelArea } from '../../features/labels/labels';
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Alert } from "@mui/material";
import { FaCopy, FaEdit, FaEye, FaEyeSlash, FaRandom, FaTrash } from "react-icons/fa";
import SearchIcon from "@mui/icons-material/Search";
import { useServer } from "../../context/ServerContext";
import {
  CrearUsuario,
  EditarUsuario,
  EliminarUsuario,
  ObtenerUsuarios,
  fetchConsolidadoCenters,
} from "../../utils/apiServer";
import Enviro from "../../assets/img/APP enviro.png";
import Nanoox from "../../assets/img/APP-nanoox-1 (003).png";
import Flux from "../../assets/img/APP-nanoox-Bolt.png";
import Scapp from "../../assets/img/APP-scapp.png";
import PSS from "../../assets/img/APP pss.png";
import "./style.css";

const servicesList = [
  { value: "enviro", label: "Enviro", icon: Enviro },
  { value: "nanoox", label: "Nanoox", icon: Nanoox },
  { value: "flux", label: "Flux", icon: Flux },
  { value: "pss", label: "PSS", icon: PSS },
  { value: "scapp", label: "Scapp", icon: Scapp },
];

const serviceKeys = servicesList.map((service) => service.value);

const roles = [
  { value: "Gerencia", label: "Gerencia" },
  { value: "Jefe Region", label: "Jefe de Region" },
  { value: "Jefe Area", label: "Jefe de Area" },
  { value: "Jefe Centro", label: "Jefe de Centro" },
  { value: "Operario", label: "Operario" },
];

const detailedRoles = ["Gerencia", "Jefe Centro", "Operario"];

const createEmptyUserDraft = () => ({
  username: "",
  password: "",
  confirmPassword: "",
  correo: "",
  cargo: "",
  regiones: [],
  areas: [],
  centros: [],
  serviciosPorArea: {},
  serviciosPorCentro: {},
  phonesByScope: {},
  phoneBook: [],
  phoneBookInput: "",
  modo: "detallado",
});

const formatNombre = (str) => {
  if (!str) return "";

  let text = String(str).replace(/-/g, " ").toLowerCase();

  if (text.startsWith("los")) {
    text = text.replace(/^los/, "Los ");
  } else if (text.startsWith("puerto")) {
    text = text.replace(/^puerto/, "Puerto ");
  }

  return text
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const unique = (items) => [...new Set(items)];

const isServiceBlock = (obj = {}) =>
  Object.keys(obj).length > 0 &&
  Object.keys(obj).every((key) => serviceKeys.includes(key));

const buildScopeKey = (type, value) => `${type}:${value}`;

const sanitizePhone = (value) => String(value || "").replace(/\D/g, "");
const isConfiguredPassword = (value) =>
  typeof value === "string" && value.length === 12 && /[A-Z]/.test(value) && /[a-z]/.test(value);

function transformData(consolidadoEstados) {
  const { areas = {}, centros = {}, regiones = {} } = consolidadoEstados || {};
  const transformedData = {};

  for (const regionName in regiones) {
    transformedData[regionName] = {};

    for (const areaName in areas) {
      const [regionPrefix, areaPrefix] = areaName.split("-");

      if (regionPrefix !== regionName) continue;

      transformedData[regionName][areaPrefix] = {};

      for (const centerName in centros[areaName] || {}) {
        const [centerPrefix] = centerName.split("-");
        transformedData[regionName][areaPrefix][centerPrefix] = {};
      }
    }
  }

  return transformedData;
}

const extractSelectedServices = (serviceBlock = {}) =>
  serviceKeys.filter((serviceKey) => serviceBlock[serviceKey]?.status === true);

const extractPhoneMap = (serviceBlock = {}) => {
  const phones = {};

  serviceKeys.forEach((serviceKey) => {
    const phone = sanitizePhone(serviceBlock[serviceKey]?.phone);
    if (phone) {
      phones[serviceKey] = phone;
    }
  });

  return phones;
};

const countPhoneEntries = (serviceBlock = {}) =>
  serviceKeys.reduce(
    (total, serviceKey) => total + (sanitizePhone(serviceBlock[serviceKey]?.phone) ? 1 : 0),
    0
  );

const createServicePermission = (status, phone) => {
  const permission = { status };
  const sanitizedPhone = sanitizePhone(phone);

  if (status && sanitizedPhone) {
    permission.phone = sanitizedPhone;
  }

  return permission;
};

const buildServiceBlock = (selectedServices = [], phones = {}) =>
  serviceKeys.reduce((acc, serviceKey) => {
    acc[serviceKey] = createServicePermission(
      selectedServices.includes(serviceKey),
      phones[serviceKey]
    );
    return acc;
  }, {});

const buildPermissionsFromDraft = (draft) => {
  const permisos = {};

  if (draft.cargo === "Jefe Region" && draft.modo === "simple") {
    Object.keys(draft.serviciosPorArea || {}).forEach((areaKey) => {
      const [region, area] = areaKey.split(":");

      if (!permisos[region]) permisos[region] = {};
      permisos[region][area] = buildServiceBlock(
        draft.serviciosPorArea[areaKey] || [],
        draft.phonesByScope?.[buildScopeKey("area", areaKey)] || {}
      );
    });

    return permisos;
  }

  if (draft.cargo === "Jefe Area" && draft.modo === "simple") {
    draft.areas.forEach((areaKey) => {
      const [region, area] = areaKey.split(":");

      if (!permisos[region]) permisos[region] = {};
      permisos[region][area] = buildServiceBlock(
        draft.serviciosPorArea?.[areaKey] || [],
        draft.phonesByScope?.[buildScopeKey("area", areaKey)] || {}
      );
    });

    return permisos;
  }

  draft.centros.forEach((centerKey) => {
    const [region, area, centro] = centerKey.split(":");

    if (!permisos[region]) permisos[region] = {};
    if (!permisos[region][area]) permisos[region][area] = {};

    const centroId = `${centro}-${area}-${region}`;
    permisos[region][area][centroId] = buildServiceBlock(
      draft.serviciosPorCentro?.[centerKey] || [],
      draft.phonesByScope?.[buildScopeKey("center", centerKey)] || {}
    );
  });

  return permisos;
};

const mapUserFromBackend = (user) => {
  const mappedUser = createEmptyUserDraft();
  const regiones = [];
  const areas = [];
  const centros = [];
  const serviciosPorArea = {};
  const serviciosPorCentro = {};
  const phonesByScope = {};
  const phoneBook = [];
  let hasSimplePermissions = false;

  Object.entries(user.permisos || {}).forEach(([region, areasObject]) => {
    regiones.push(region);

    Object.entries(areasObject || {}).forEach(([area, areaObject]) => {
      const areaKey = `${region}:${area}`;
      areas.push(areaKey);

      if (isServiceBlock(areaObject)) {
        hasSimplePermissions = true;
        serviciosPorArea[areaKey] = extractSelectedServices(areaObject);

        const phoneMap = extractPhoneMap(areaObject);
        if (Object.keys(phoneMap).length > 0) {
          phonesByScope[buildScopeKey("area", areaKey)] = phoneMap;
          Object.values(phoneMap).forEach((phone) => phoneBook.push(phone));
        }
        return;
      }

      Object.entries(areaObject || {}).forEach(([centerId, serviceBlock]) => {
        const centroNombre = String(centerId).split("-")[0];
        const centerKey = `${region}:${area}:${centroNombre}`;

        centros.push(centerKey);
        serviciosPorCentro[centerKey] = extractSelectedServices(serviceBlock);

        const phoneMap = extractPhoneMap(serviceBlock);
        if (Object.keys(phoneMap).length > 0) {
          phonesByScope[buildScopeKey("center", centerKey)] = phoneMap;
          Object.values(phoneMap).forEach((phone) => phoneBook.push(phone));
        }
      });
    });
  });

  mappedUser.username = user.username || "";
  mappedUser.correo = user.correo || "";
  mappedUser.cargo = user.cargo || "";
  mappedUser.regiones = unique(regiones);
  mappedUser.areas = unique(areas);
  mappedUser.centros = unique(centros);
  mappedUser.serviciosPorArea = serviciosPorArea;
  mappedUser.serviciosPorCentro = serviciosPorCentro;
  mappedUser.phonesByScope = phonesByScope;
  mappedUser.phoneBook = unique(phoneBook);
  mappedUser.modo =
    ["Jefe Region", "Jefe Area"].includes(user.cargo) && hasSimplePermissions
      ? "simple"
      : "detallado";

  return mappedUser;
};

const getUserPermissionSummary = (user) => {
  const summary = [];

  Object.entries(user.permisos || {}).forEach(([region, areasObject]) => {
    const regionParts = [];

    Object.entries(areasObject || {}).forEach(([area, areaObject]) => {
      if (isServiceBlock(areaObject)) {
        const activeServices = extractSelectedServices(areaObject).length;
        const phoneCount = countPhoneEntries(areaObject);
        regionParts.push(
          `${labelArea({ region, area }, formatNombre(area))}: ${activeServices} apps${phoneCount ? ` · ${phoneCount} WA` : ""}`
        );
        return;
      }

      const activeCenters = Object.values(areaObject || {}).filter((serviceBlock) =>
        serviceKeys.some((serviceKey) => serviceBlock?.[serviceKey]?.status)
      ).length;
      const phoneCount = Object.values(areaObject || {}).reduce(
        (total, serviceBlock) => total + countPhoneEntries(serviceBlock),
        0
      );

      regionParts.push(
        `${labelArea({ region, area }, formatNombre(area))}: ${activeCenters} ${activeCenters === 1 ? "centro" : "centros"}${
          phoneCount ? ` · ${phoneCount} WA` : ""
        }`
      );
    });

    if (regionParts.length > 0) {
      summary.push({
        region: formatNombre(region),
        regionLabel: labelRegion(region, formatNombre(region)),
        areaIds: Object.keys(areasObject || {}).join(" "),
        detail: regionParts.join(" | "),
      });
    }
  });

  return summary;
};

export default function CRUDUsuarios({ translate }) {
  const labelsRevision = useLabelVersion();
  const { servidor } = useServer();
  const { token } = useParams();
  const [usuarios, setUsuarios] = useState([]);
  const [permisosBase, setPermisosBase] = useState({});
  const [mostrarModal, setMostrarModal] = useState(false);
  const [nuevosUsuarios, setNuevosUsuarios] = useState([createEmptyUserDraft()]);
  const [search, setSearch] = useState("");
  const [rolesFiltro, setRolesFiltro] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [usuariosPorPagina] = useState(8);
  const [alertMessage, setAlertMessage] = useState(null);
  const [alertSeverity, setAlertSeverity] = useState("success");
  const [flashNotice, setFlashNotice] = useState(null);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [usuarioOriginal, setUsuarioOriginal] = useState(null);
  const [usuarioAEliminar, setUsuarioAEliminar] = useState(null);
  const [confirmarEliminar, setConfirmarEliminar] = useState(false);
  const [showCreateValidation, setShowCreateValidation] = useState(false);
  const [selectedUsernames, setSelectedUsernames] = useState([]);
  const [bulkRegion, setBulkRegion] = useState("");
  const [bulkArea, setBulkArea] = useState("");
  const [bulkCenter, setBulkCenter] = useState("");
  const [bulkService, setBulkService] = useState("");
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [visiblePasswords, setVisiblePasswords] = useState({});

  const actualizarListaUsuarios = async () => {
    try {
      const usuariosObtenidos = await ObtenerUsuarios(servidor);
      const usuariosOrdenados = [...usuariosObtenidos].sort((a, b) =>
        String(a.username || "").localeCompare(String(b.username || ""))
      );
      setUsuarios(usuariosOrdenados);
    } catch (error) {
      console.error("Error al obtener usuarios:", error);
    }
  };

  useEffect(() => {
    actualizarListaUsuarios();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const result = await fetchConsolidadoCenters("HOME", servidor);
      setPermisosBase(transformData(result));
    };

    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [servidor]);

  const usuariosFiltrados = useMemo(() => {
    return usuarios.filter((usuario) => {
      const roleMatch =
        rolesFiltro.length === 0 || rolesFiltro.includes(usuario.cargo);
      const searchValue = search.trim().toLowerCase();

      if (!searchValue) return roleMatch;

      const permissionSummary = getUserPermissionSummary(usuario)
        .map((entry) => `${entry.region} ${entry.regionLabel} ${entry.areaIds} ${entry.detail}`.toLowerCase())
        .join(" ");

      const combined = `${usuario.username || ""} ${usuario.correo || ""} ${
        usuario.cargo || ""
      } ${permissionSummary}`.toLowerCase();

      return roleMatch && combined.includes(searchValue);
    });
  }, [rolesFiltro, search, usuarios, labelsRevision]); // eslint-disable-line react-hooks/exhaustive-deps -- La versión invalida labels del resolvedor en memoria.

  const totalPaginas = Math.max(1, Math.ceil(usuariosFiltrados.length / usuariosPorPagina));
  const indiceInicial = (currentPage - 1) * usuariosPorPagina;
  const usuariosPagina = usuariosFiltrados.slice(
    indiceInicial,
    indiceInicial + usuariosPorPagina
  );
  const bulkAreas = bulkRegion ? Object.keys(permisosBase?.[bulkRegion] || {}) : [];
  const bulkCenters = bulkRegion && bulkArea ? Object.keys(permisosBase?.[bulkRegion]?.[bulkArea] || {}) : [];
  const selectedVisibleUsers = usuariosPagina.filter((usuario) => selectedUsernames.includes(usuario.username));
  const allVisibleUsersSelected = usuariosPagina.length > 0 && selectedVisibleUsers.length === usuariosPagina.length;

  useEffect(() => {
    if (currentPage > totalPaginas) {
      setCurrentPage(totalPaginas);
    }
  }, [currentPage, totalPaginas]);

  useEffect(() => {
    if (!flashNotice) return undefined;

    const timeout = window.setTimeout(() => {
      setFlashNotice(null);
    }, 2000);

    return () => window.clearTimeout(timeout);
  }, [flashNotice]);

  const updateDraftAt = (index, updater) => {
    setNuevosUsuarios((prev) =>
      prev.map((draft, draftIndex) =>
        draftIndex === index ? updater(draft) : draft
      )
    );
  };

  const cleanPhonesByPrefix = (phonesByScope, prefixes) =>
    Object.fromEntries(
      Object.entries(phonesByScope || {}).filter(
        ([scopeKey]) => !prefixes.some((prefix) => scopeKey.startsWith(prefix))
      )
    );

  const handleChange = (index, field, value) => {
    setShowCreateValidation(false);
    updateDraftAt(index, (draft) => {
      if (field === "cargo") {
        return {
          ...createEmptyUserDraft(),
          username: draft.username,
          correo: draft.correo,
          password: draft.password,
          confirmPassword: draft.confirmPassword,
          cargo: value,
        };
      }

      return {
        ...draft,
        [field]: value,
      };
    });
  };

  const agregarTelefonoCompartido = (index) => {
    updateDraftAt(index, (draft) => {
      const sanitizedPhone = sanitizePhone(draft.phoneBookInput);

      if (!sanitizedPhone) {
        setAlertMessage("Ingresa un numero valido para agregarlo.");
        setAlertSeverity("warning");
        return draft;
      }

      return {
        ...draft,
        phoneBook: unique([...(draft.phoneBook || []), sanitizedPhone]),
        phoneBookInput: "",
      };
    });
  };

  const handleToggleRoleFilter = (role) => {
    setRolesFiltro((prev) =>
      prev.includes(role) ? prev.filter((item) => item !== role) : [...prev, role]
    );
    setCurrentPage(1);
  };

  const handleRegionSelect = (index, region) => {
    updateDraftAt(index, (draft) => {
      const regionSelected = draft.regiones.includes(region);
      const regiones = regionSelected
        ? draft.regiones.filter((item) => item !== region)
        : [...draft.regiones, region];

      if (!regionSelected) {
        return { ...draft, regiones: unique(regiones) };
      }

      const areas = draft.areas.filter((areaKey) => !areaKey.startsWith(`${region}:`));
      const centros = draft.centros.filter(
        (centerKey) => !centerKey.startsWith(`${region}:`)
      );
      const serviciosPorArea = Object.fromEntries(
        Object.entries(draft.serviciosPorArea || {}).filter(
          ([areaKey]) => !areaKey.startsWith(`${region}:`)
        )
      );
      const serviciosPorCentro = Object.fromEntries(
        Object.entries(draft.serviciosPorCentro || {}).filter(
          ([centerKey]) => !centerKey.startsWith(`${region}:`)
        )
      );
      const phonesByScope = cleanPhonesByPrefix(draft.phonesByScope, [
        buildScopeKey("area", `${region}:`),
        buildScopeKey("center", `${region}:`),
      ]);

      return {
        ...draft,
        regiones,
        areas,
        centros,
        serviciosPorArea,
        serviciosPorCentro,
        phonesByScope,
      };
    });
  };

  const handleAreaSelect = (index, areaKey) => {
    updateDraftAt(index, (draft) => {
      const [region] = areaKey.split(":");
      const areaSelected = draft.areas.includes(areaKey);
      const areas = areaSelected
        ? draft.areas.filter((item) => item !== areaKey)
        : [...draft.areas, areaKey];

      if (!areaSelected) {
        return {
          ...draft,
          regiones: unique([...draft.regiones, region]),
          areas: unique(areas),
        };
      }

      const centros = draft.centros.filter(
        (centerKey) => !centerKey.startsWith(`${areaKey}:`)
      );
      const serviciosPorArea = Object.fromEntries(
        Object.entries(draft.serviciosPorArea || {}).filter(
          ([storedAreaKey]) => storedAreaKey !== areaKey
        )
      );
      const serviciosPorCentro = Object.fromEntries(
        Object.entries(draft.serviciosPorCentro || {}).filter(
          ([centerKey]) => !centerKey.startsWith(`${areaKey}:`)
        )
      );
      const phonesByScope = cleanPhonesByPrefix(draft.phonesByScope, [
        buildScopeKey("area", areaKey),
        buildScopeKey("center", `${areaKey}:`),
      ]);

      return {
        ...draft,
        areas,
        centros,
        serviciosPorArea,
        serviciosPorCentro,
        phonesByScope,
      };
    });
  };

  const handleCentroSelect = (index, centerKey) => {
    updateDraftAt(index, (draft) => {
      const [region, area] = centerKey.split(":");
      const areaKey = `${region}:${area}`;
      const centerSelected = draft.centros.includes(centerKey);
      const centros = centerSelected
        ? draft.centros.filter((item) => item !== centerKey)
        : [...draft.centros, centerKey];

      if (!centerSelected) {
        return {
          ...draft,
          regiones: unique([...draft.regiones, region]),
          areas:
            draft.modo === "detallado" || detailedRoles.includes(draft.cargo)
              ? unique([...draft.areas, areaKey])
              : draft.areas,
          centros: unique(centros),
        };
      }

      const serviciosPorCentro = Object.fromEntries(
        Object.entries(draft.serviciosPorCentro || {}).filter(
          ([storedCenterKey]) => storedCenterKey !== centerKey
        )
      );
      const phonesByScope = cleanPhonesByPrefix(draft.phonesByScope, [
        buildScopeKey("center", centerKey),
      ]);

      return {
        ...draft,
        centros,
        serviciosPorCentro,
        phonesByScope,
      };
    });
  };

  const handleScopedServiceToggle = (index, scopeType, scopeValue, service) => {
    updateDraftAt(index, (draft) => {
      const mapKey = scopeType === "area" ? "serviciosPorArea" : "serviciosPorCentro";
      const currentServices = draft[mapKey]?.[scopeValue] || [];
      const selected = currentServices.includes(service);
      const nextServices = selected
        ? currentServices.filter((item) => item !== service)
        : [...currentServices, service];

      const nextMap = { ...(draft[mapKey] || {}) };
      nextMap[scopeValue] = nextServices;

      const scopeKey = buildScopeKey(scopeType, scopeValue);
      const nextPhonesByScope = { ...(draft.phonesByScope || {}) };

      if (selected && nextPhonesByScope[scopeKey]?.[service]) {
        const nextPhones = { ...nextPhonesByScope[scopeKey] };
        delete nextPhones[service];
        if (Object.keys(nextPhones).length === 0) {
          delete nextPhonesByScope[scopeKey];
        } else {
          nextPhonesByScope[scopeKey] = nextPhones;
        }
      }

      return {
        ...draft,
        [mapKey]: nextMap,
        phonesByScope: nextPhonesByScope,
      };
    });
  };

  const handlePhoneChange = (index, scopeType, scopeValue, service, value) => {
    const sanitized = sanitizePhone(value);

    updateDraftAt(index, (draft) => {
      const scopeKey = buildScopeKey(scopeType, scopeValue);
      const nextPhonesByScope = { ...(draft.phonesByScope || {}) };
      const scopePhones = { ...(nextPhonesByScope[scopeKey] || {}) };

      if (sanitized) {
        scopePhones[service] = sanitized;
        nextPhonesByScope[scopeKey] = scopePhones;
      } else {
        delete scopePhones[service];
        if (Object.keys(scopePhones).length === 0) {
          delete nextPhonesByScope[scopeKey];
        } else {
          nextPhonesByScope[scopeKey] = scopePhones;
        }
      }

      return {
        ...draft,
        phonesByScope: nextPhonesByScope,
      };
    });
  };

  const agregarUsuarioCampo = () => {
    const currentDraft = nuevosUsuarios[nuevosUsuarios.length - 1];
    const validationError = validarDraft(currentDraft);

    if (validationError) {
      setAlertMessage(validationError);
      setAlertSeverity("warning");
      setShowCreateValidation(true);
      return;
    }

    setShowCreateValidation(false);
    setNuevosUsuarios((prev) => [...prev, createEmptyUserDraft()]);
  };

  const secureRandomIndex = (max) => {
    const values = new Uint32Array(1);
    window.crypto.getRandomValues(values);
    return values[0] % max;
  };

  const generatePassword = (index) => {
    const uppercase = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const lowercase = "abcdefghijklmnopqrstuvwxyz";
    const characters = uppercase + lowercase;
    const passwordCharacters = [
      uppercase[secureRandomIndex(uppercase.length)],
      lowercase[secureRandomIndex(lowercase.length)],
    ];

    while (passwordCharacters.length < 12) {
      passwordCharacters.push(characters[secureRandomIndex(characters.length)]);
    }

    for (let position = passwordCharacters.length - 1; position > 0; position -= 1) {
      const randomPosition = secureRandomIndex(position + 1);
      [passwordCharacters[position], passwordCharacters[randomPosition]] = [passwordCharacters[randomPosition], passwordCharacters[position]];
    }

    const generatedPassword = passwordCharacters.join("");
    updateDraftAt(index, (draft) => ({
      ...draft,
      password: generatedPassword,
      confirmPassword: "",
    }));
    setVisiblePasswords((prev) => ({ ...prev, [index]: true }));
    setShowCreateValidation(false);
  };

  const copyPassword = async (password) => {
    if (!password) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(password);
      } else {
        const temporaryInput = document.createElement("textarea");
        temporaryInput.value = password;
        temporaryInput.style.position = "fixed";
        temporaryInput.style.opacity = "0";
        document.body.appendChild(temporaryInput);
        temporaryInput.select();
        document.execCommand("copy");
        temporaryInput.remove();
      }
      setFlashNotice({ type: "success", message: "Contraseña copiada" });
    } catch (error) {
      console.error("No fue posible copiar la contraseña:", error);
      setAlertMessage("No fue posible copiar la contraseña.");
      setAlertSeverity("warning");
    }
  };

  const togglePasswordVisibility = (index) => {
    setVisiblePasswords((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const toggleUserSelection = (username) => {
    setSelectedUsernames((prev) =>
      prev.includes(username)
        ? prev.filter((item) => item !== username)
        : [...prev, username]
    );
  };

  const toggleVisibleUsers = () => {
    const visibleNames = usuariosPagina.map((usuario) => usuario.username);
    setSelectedUsernames((prev) => {
      if (allVisibleUsersSelected) {
        return prev.filter((username) => !visibleNames.includes(username));
      }
      return unique([...prev, ...visibleNames]);
    });
  };

  const clearBulkSelection = () => {
    setSelectedUsernames([]);
    setBulkRegion("");
    setBulkArea("");
    setBulkCenter("");
    setBulkService("");
  };

  const addCenterServiceToSelectedUsers = async () => {
    if (!selectedUsernames.length || !bulkRegion || !bulkArea || !bulkCenter || !bulkService) {
      setAlertMessage("Selecciona usuarios, centro y servicio antes de aplicar.");
      setAlertSeverity("warning");
      return;
    }

    const selectedUsers = usuarios.filter((usuario) => selectedUsernames.includes(usuario.username));
    const centerId = `${bulkCenter}-${bulkArea}-${bulkRegion}`;
    const permissionPatch = {
      [bulkRegion]: {
        [bulkArea]: {
          [centerId]: {
            [bulkService]: { status: true },
          },
        },
      },
    };
    const edits = selectedUsers.map((usuario) => ({
      username: usuario.username,
      updates: { agregar_permisos: permissionPatch },
    }));

    try {
      setBulkUpdating(true);
      const result = await EditarUsuario({ edits }, servidor, token);
      const failed = (result?.results || []).filter((item) => item.status !== "ok");
      if (failed.length) {
        const failureDetails = failed
          .map((item) => `${item.username}: ${item.msg || item.status}`)
          .join(" · ");
        throw new Error(`No se pudo actualizar ${failed.length} usuario${failed.length === 1 ? "" : "s"}. ${failureDetails}`);
      }
      await actualizarListaUsuarios();
      setFlashNotice({
        type: "success",
        message: `${formatNombre(bulkCenter)} · ${bulkService.toUpperCase()} agregado a ${edits.length} usuario${edits.length === 1 ? "" : "s"}`,
      });
      clearBulkSelection();
    } catch (error) {
      console.error("Error al asignar centro y servicio en lote:", error);
      setAlertMessage(error.message || "No fue posible actualizar los usuarios seleccionados.");
      setAlertSeverity("error");
    } finally {
      setBulkUpdating(false);
    }
  };

  const eliminarUsuarioFormulario = (index) => {
    setNuevosUsuarios((prev) => {
      if (prev.length === 1) return [createEmptyUserDraft()];
      return prev.filter((_, draftIndex) => draftIndex !== index);
    });
  };

  const abrirCrearUsuario = () => {
    setModoEdicion(false);
    setUsuarioOriginal(null);
    setNuevosUsuarios([createEmptyUserDraft()]);
    setVisiblePasswords({});
    setShowCreateValidation(false);
    setMostrarModal(true);
  };

  const abrirModalEdicion = (usuario) => {
    setModoEdicion(true);
    setUsuarioOriginal(usuario);
    setNuevosUsuarios([mapUserFromBackend(usuario)]);
    setVisiblePasswords({});
    setMostrarModal(true);
  };

  const limpiarModal = () => {
    setMostrarModal(false);
    setModoEdicion(false);
    setUsuarioOriginal(null);
    setNuevosUsuarios([createEmptyUserDraft()]);
    setVisiblePasswords({});
    setShowCreateValidation(false);
  };

  const validarDraft = (draft) => {
    if (!draft.username?.trim()) return "Debes ingresar un username.";
    if (!modoEdicion && !draft.password?.trim()) return "Debes ingresar una password.";
    if (draft.password?.trim()) {
      if (draft.password.length !== 12) return "La contraseña debe tener exactamente 12 caracteres.";
      if (!/[A-Z]/.test(draft.password)) return "La contraseña debe incluir al menos una mayúscula.";
      if (!/[a-z]/.test(draft.password)) return "La contraseña debe incluir al menos una minúscula.";
      if (draft.password !== draft.confirmPassword) return "Las contraseñas no coinciden.";
    } else if (draft.confirmPassword) {
      return "Ingresa la contraseña antes de repetirla.";
    }
    if (!draft.correo?.trim()) return "Debes ingresar un correo.";
    if (!draft.cargo?.trim()) return "Debes seleccionar un cargo.";

    if (draft.cargo === "Jefe Region" && draft.modo === "simple") {
      if (Object.keys(draft.serviciosPorArea || {}).length === 0) {
        return "Selecciona al menos un area con permisos para este usuario.";
      }
      return null;
    }

    if (draft.cargo === "Jefe Area" && draft.modo === "simple") {
      if ((draft.areas || []).length === 0) {
        return "Selecciona al menos un area para este usuario.";
      }
      return null;
    }

    if ((draft.centros || []).length === 0) {
      return "Selecciona al menos un centro para este usuario.";
    }

    return null;
  };

  const guardarUsuarios = async () => {
    for (const draft of nuevosUsuarios) {
      const error = validarDraft(draft);
      if (error) {
        setAlertMessage(error);
        setAlertSeverity("warning");
        setShowCreateValidation(true);
        return;
      }
    }

    const usuariosFormateados = nuevosUsuarios.map((draft) => ({
      username: draft.username.trim(),
      password: draft.password,
      correo: draft.correo.trim(),
      cargo: draft.cargo,
      permisos: buildPermissionsFromDraft(draft),
    }));

    try {
      const createResult = await CrearUsuario(
        usuariosFormateados,
        setAlertMessage,
        setAlertSeverity,
        translate,
        servidor,
        token
      );
      await actualizarListaUsuarios();
      const hasBatchErrors =
        Array.isArray(createResult?.resultados) &&
        createResult.resultados.some((result) => result.estado !== 201);

      if (!hasBatchErrors) {
        setShowCreateValidation(false);
        setAlertMessage(null);
        setFlashNotice({
          type: "success",
          message:
            usuariosFormateados.length > 1
              ? "Usuarios creados con exito"
              : "Usuario creado con exito",
        });
      }
      limpiarModal();
    } catch (error) {
      console.error("Error al guardar usuarios:", error);
    }
  };

  const guardarCambios = async () => {
    const draft = nuevosUsuarios[0];
    const validationError = validarDraft(draft);

    if (validationError) {
      setAlertMessage(validationError);
      setAlertSeverity("warning");
      setShowCreateValidation(true);
      return;
    }

    const updates = {};
    const correo = draft.correo.trim();
    const permisos = buildPermissionsFromDraft(draft);

    if (correo !== usuarioOriginal?.correo) updates.correo = correo;
    if (draft.cargo !== usuarioOriginal?.cargo) updates.cargo = draft.cargo;
    if (draft.password?.trim()) updates.password = draft.password;

    if (JSON.stringify(permisos) !== JSON.stringify(usuarioOriginal?.permisos || {})) {
      updates.permisos = permisos;
    }

    if (Object.keys(updates).length === 0) {
      setAlertMessage("No se detectaron cambios.");
      setAlertSeverity("info");
      return;
    }

    try {
      await EditarUsuario(
        {
          edits: [
            {
              username: draft.username,
              updates,
            },
          ],
        },
        servidor,
        token
      );

      setAlertMessage(null);
      setAlertSeverity("success");
      setFlashNotice({
        type: "success",
        message: "Usuario editado con exito",
      });
      await actualizarListaUsuarios();
      limpiarModal();
    } catch (error) {
      console.error("Error al editar usuario:", error);
      setAlertMessage("No fue posible actualizar el usuario.");
      setAlertSeverity("error");
    }
  };

  const eliminarUsuario = (usuario) => {
    setUsuarioAEliminar(usuario);
    setConfirmarEliminar(true);
  };

  const confirmarEliminacion = async () => {
    if (!usuarioAEliminar) return;

    const clientName = localStorage.getItem("username");

    try {
      await EliminarUsuario(clientName, usuarioAEliminar, servidor);
      setAlertMessage("Usuario eliminado correctamente.");
      setAlertSeverity("success");
      await actualizarListaUsuarios();
    } catch (error) {
      console.error("Error al eliminar usuario:", error);
      setAlertMessage("Error al eliminar usuario.");
      setAlertSeverity("error");
    } finally {
      setConfirmarEliminar(false);
      setUsuarioAEliminar(null);
    }
  };

  const renderServiceSelector = (draft, userIndex, scopeType, scopeValue) => {
    const mapKey = scopeType === "area" ? "serviciosPorArea" : "serviciosPorCentro";
    const selectedServices = draft[mapKey]?.[scopeValue] || [];
    const phonesByScope = draft.phonesByScope?.[buildScopeKey(scopeType, scopeValue)] || {};

    return (
      <div className="crud-service-grid">
        {servicesList.map((service) => {
          const selected = selectedServices.includes(service.value);
          const phoneValue = phonesByScope[service.value] || "";

          return (
            <div
              key={`${scopeType}-${scopeValue}-${service.value}`}
              className={`crud-service-card ${selected ? "is-active" : ""}`}
            >
              <button
                type="button"
                className="crud-service-toggle"
                onClick={() =>
                  handleScopedServiceToggle(
                    userIndex,
                    scopeType,
                    scopeValue,
                    service.value
                  )
                }
              >
                <img src={service.icon} alt={service.label} />
                <span>{service.label}</span>
              </button>

              {selected ? (
                <div className="crud-phone-wrap">
                  {(draft.phoneBook || []).length > 0 ? (
                    <div className="crud-shared-phones">
                      <span>Numeros guardados</span>
                      <div className="crud-shared-phone-list">
                        {draft.phoneBook.map((savedPhone) => (
                          <button
                            key={`${scopeType}-${scopeValue}-${service.value}-${savedPhone}`}
                            type="button"
                            className={`crud-shared-phone-chip ${
                              phoneValue === savedPhone ? "is-selected" : ""
                            }`}
                            onClick={() =>
                              handlePhoneChange(
                                userIndex,
                                scopeType,
                                scopeValue,
                                service.value,
                                savedPhone
                              )
                            }
                          >
                            {savedPhone}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  <label htmlFor={`${scopeType}-${scopeValue}-${service.value}-phone`}>
                    Phone / WhatsApp
                  </label>
                  <input
                    id={`${scopeType}-${scopeValue}-${service.value}-phone`}
                    className="crud-phone-input"
                    type="text"
                    inputMode="numeric"
                    maxLength={15}
                    placeholder="56912345678"
                    value={phoneValue}
                    onChange={(event) =>
                      handlePhoneChange(
                        userIndex,
                        scopeType,
                        scopeValue,
                        service.value,
                        event.target.value
                      )
                    }
                  />
                </div>
              ) : (
                <span className="crud-service-hint">Sin WhatsApp</span>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderDetailedCenters = (draft, userIndex, region, area) => {
    return Object.keys(permisosBase?.[region]?.[area] || {}).map((center) => {
      const centerKey = `${region}:${area}:${center}`;
      const selected = draft.centros.includes(centerKey);

      return (
        <div key={centerKey} className="crud-scope-card crud-scope-card-center">
          <button
            type="button"
            className={`crud-scope-toggle ${selected ? "is-selected" : ""}`}
            onClick={() => handleCentroSelect(userIndex, centerKey)}
          >
            {labelCentro({ region, area, centro: center }, formatNombre(center))}
          </button>

          {selected ? renderServiceSelector(draft, userIndex, "center", centerKey) : null}
        </div>
      );
    });
  };

  const renderPermissionBuilder = (draft, userIndex) => {
    if (!draft.cargo) {
      return (
        <div className="crud-empty-state soft">
          Selecciona un cargo para comenzar a configurar permisos.
        </div>
      );
    }

    if (draft.cargo === "Jefe Region" && draft.modo === "simple") {
      return Object.keys(permisosBase).map((region) => {
        const selectedRegion = draft.regiones.includes(region);

        return (
          <details
            key={`${userIndex}-${region}`}
            className="crud-region-card"
            open={selectedRegion}
          >
            <summary>
              <button
                type="button"
                className={`crud-scope-toggle ${selectedRegion ? "is-selected" : ""}`}
                onClick={(event) => {
                  event.preventDefault();
                  handleRegionSelect(userIndex, region);
                }}
              >
                {labelRegion(region, formatNombre(region))}
              </button>
              <span>
                {selectedRegion
                  ? "Configura servicios y telefono por area."
                  : "Activa la region para editarla."}
              </span>
            </summary>

            {selectedRegion ? (
              <div className="crud-area-stack">
                {Object.keys(permisosBase[region] || {}).map((area) => {
                  const areaKey = `${region}:${area}`;
                  return (
                    <div key={areaKey} className="crud-scope-card">
                      <div className="crud-scope-header">
                        <strong>{labelArea({ region, area }, formatNombre(area))}</strong>
                        <span>Permisos por area</span>
                      </div>
                      {renderServiceSelector(draft, userIndex, "area", areaKey)}
                    </div>
                  );
                })}
              </div>
            ) : null}
          </details>
        );
      });
    }

    if (draft.cargo === "Jefe Region" && draft.modo === "detallado") {
      return Object.keys(permisosBase).map((region) => {
        const selectedRegion = draft.regiones.includes(region);

        return (
          <details
            key={`${userIndex}-${region}`}
            className="crud-region-card"
            open={selectedRegion}
          >
            <summary>
              <button
                type="button"
                className={`crud-scope-toggle ${selectedRegion ? "is-selected" : ""}`}
                onClick={(event) => {
                  event.preventDefault();
                  handleRegionSelect(userIndex, region);
                }}
              >
                {labelRegion(region, formatNombre(region))}
              </button>
              <span>
                {selectedRegion
                  ? "Selecciona centros y define servicios con phone."
                  : "Activa la region para ver sus centros."}
              </span>
            </summary>

            {selectedRegion ? (
              <div className="crud-area-stack">
                {Object.keys(permisosBase[region] || {}).map((area) => (
                  <div key={`${region}:${area}`} className="crud-scope-card">
                    <div className="crud-scope-header">
                      <strong>{labelArea({ region, area }, formatNombre(area))}</strong>
                      <span>Centros disponibles</span>
                    </div>
                    {renderDetailedCenters(draft, userIndex, region, area)}
                  </div>
                ))}
              </div>
            ) : null}
          </details>
        );
      });
    }

    if (draft.cargo === "Jefe Area" && draft.modo === "simple") {
      return Object.keys(permisosBase).map((region) => (
        <details key={`${userIndex}-${region}`} className="crud-region-card" open>
          <summary>
            <strong>{labelRegion(region, formatNombre(region))}</strong>
            <span>Activa las areas que administrara este usuario.</span>
          </summary>

          <div className="crud-area-stack">
            {Object.keys(permisosBase[region] || {}).map((area) => {
              const areaKey = `${region}:${area}`;
              const selectedArea = draft.areas.includes(areaKey);

              return (
                <div key={areaKey} className="crud-scope-card">
                  <button
                    type="button"
                    className={`crud-scope-toggle ${selectedArea ? "is-selected" : ""}`}
                    onClick={() => handleAreaSelect(userIndex, areaKey)}
                  >
                    {labelArea({ region, area }, formatNombre(area))}
                  </button>

                  {selectedArea
                    ? renderServiceSelector(draft, userIndex, "area", areaKey)
                    : null}
                </div>
              );
            })}
          </div>
        </details>
      ));
    }

    if (draft.cargo === "Jefe Area" && draft.modo === "detallado") {
      return Object.keys(permisosBase).map((region) => (
        <details key={`${userIndex}-${region}`} className="crud-region-card" open>
          <summary>
            <strong>{labelRegion(region, formatNombre(region))}</strong>
            <span>Selecciona areas y luego centros especificos.</span>
          </summary>

          <div className="crud-area-stack">
            {Object.keys(permisosBase[region] || {}).map((area) => {
              const areaKey = `${region}:${area}`;
              const selectedArea = draft.areas.includes(areaKey);

              return (
                <div key={areaKey} className="crud-scope-card">
                  <button
                    type="button"
                    className={`crud-scope-toggle ${selectedArea ? "is-selected" : ""}`}
                    onClick={() => handleAreaSelect(userIndex, areaKey)}
                  >
                    {labelArea({ region, area }, formatNombre(area))}
                  </button>

                  {selectedArea ? (
                    <div className="crud-center-stack">
                      {renderDetailedCenters(draft, userIndex, region, area)}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </details>
      ));
    }

    if (detailedRoles.includes(draft.cargo)) {
      return Object.keys(permisosBase).map((region) => (
        <details key={`${userIndex}-${region}`} className="crud-region-card" open>
          <summary>
            <strong>{labelRegion(region, formatNombre(region))}</strong>
            <span>Permisos por centro para {draft.cargo.toLowerCase()}.</span>
          </summary>

          <div className="crud-area-stack">
            {Object.keys(permisosBase[region] || {}).map((area) => (
              <div key={`${region}:${area}`} className="crud-scope-card">
                <div className="crud-scope-header">
                  <strong>{labelArea({ region, area }, formatNombre(area))}</strong>
                  <span>
                    {Object.keys(permisosBase[region][area] || {}).length} centros
                  </span>
                </div>
                {renderDetailedCenters(draft, userIndex, region, area)}
              </div>
            ))}
          </div>
        </details>
      ));
    }

    return null;
  };

  return (
    <div className="crud-users-view">
      <section className="crud-users-hero">
        <div>
          <span className="crud-users-hero-eyebrow">Administracion</span>
          <h1>Usuarios</h1>
          <p>
            Administra accesos por region, area y centro. Cada servicio activo puede incluir un <code>phone</code> para WhatsApp.
          </p>
        </div>

        {!mostrarModal ? (
          <button type="button" className="crud-primary-button" onClick={abrirCrearUsuario}>
            Crear usuario
          </button>
        ) : null}
      </section>

      <nav className="crud-view-tabs" aria-label="Vistas de usuarios">
        <button
          type="button"
          className={!mostrarModal ? "is-active" : ""}
          onClick={limpiarModal}
        >
          Ver usuarios
        </button>
        <button
          type="button"
          className={mostrarModal && !modoEdicion ? "is-active" : ""}
          onClick={() => {
            if (!mostrarModal || modoEdicion) abrirCrearUsuario();
          }}
        >
          Crear usuarios{mostrarModal && !modoEdicion && nuevosUsuarios.length > 1 ? ` · ${nuevosUsuarios.length - 1} listos` : ""}
        </button>
        {mostrarModal && modoEdicion ? (
          <button type="button" className="is-active" aria-current="page">
            Editar · {nuevosUsuarios[0]?.username || "usuario"}
          </button>
        ) : null}
      </nav>

      {!mostrarModal ? (
      <section className="crud-users-dashboard">
        <aside className="crud-filter-panel">
          <div className="crud-search-box">
            <SearchIcon className="crud-search-icon" />
            <input
              type="text"
              placeholder={translate("buscar_user")}
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setCurrentPage(1);
              }}
            />
          </div>

          <div className="crud-filter-card">
            <h3>Roles</h3>
            <div className="crud-filter-pills">
              {roles.map((role) => {
                const selected = rolesFiltro.includes(role.value);

                return (
                  <button
                    key={role.value}
                    type="button"
                    className={`crud-filter-pill ${selected ? "is-selected" : ""}`}
                    onClick={() => handleToggleRoleFilter(role.value)}
                  >
                    {role.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="crud-filter-card">
            <h3>Resumen</h3>
            <div className="crud-metric">
              <span>Usuarios visibles</span>
              <strong>{usuariosFiltrados.length}</strong>
            </div>
            <div className="crud-metric">
              <span>Con phone activo</span>
              <strong>
                {usuariosFiltrados.reduce((total, usuario) => {
                  return (
                    total +
                    Object.values(usuario.permisos || {}).reduce((regionTotal, areaObject) => {
                      return (
                        regionTotal +
                        Object.values(areaObject || {}).reduce((areaTotal, node) => {
                          if (isServiceBlock(node)) {
                            return areaTotal + countPhoneEntries(node);
                          }

                          return (
                            areaTotal +
                            Object.values(node || {}).reduce(
                              (nodeTotal, serviceBlock) =>
                                nodeTotal + countPhoneEntries(serviceBlock),
                              0
                            )
                          );
                        }, 0)
                      );
                    }, 0)
                  );
                }, 0)}
              </strong>
            </div>
          </div>
        </aside>

        <div className="crud-list-panel">
          {selectedUsernames.length > 0 ? (
            <div className="crud-bulk-toolbar">
              <strong>{selectedUsernames.length} {selectedUsernames.length === 1 ? "usuario seleccionado" : "usuarios seleccionados"}</strong>
              <div className="crud-bulk-fields">
                <select
                  value={bulkRegion}
                  onChange={(event) => {
                    setBulkRegion(event.target.value);
                    setBulkArea("");
                    setBulkCenter("");
                  }}
                >
                  <option value="">Región</option>
                  {Object.keys(permisosBase).map((region) => <option key={region} value={region}>{formatNombre(region)}</option>)}
                </select>
                <select
                  value={bulkArea}
                  disabled={!bulkRegion}
                  onChange={(event) => {
                    setBulkArea(event.target.value);
                    setBulkCenter("");
                  }}
                >
                  <option value="">Área</option>
                  {bulkAreas.map((area) => <option key={area} value={area}>{formatNombre(area)}</option>)}
                </select>
                <select value={bulkCenter} disabled={!bulkArea} onChange={(event) => setBulkCenter(event.target.value)}>
                  <option value="">Centro</option>
                  {bulkCenters.map((center) => <option key={center} value={center}>{formatNombre(center)}</option>)}
                </select>
                <select value={bulkService} onChange={(event) => setBulkService(event.target.value)}>
                  <option value="">Servicio</option>
                  {servicesList.map((service) => <option key={service.value} value={service.value}>{service.label}</option>)}
                </select>
              </div>
              <button
                type="button"
                className="crud-bulk-apply"
                disabled={bulkUpdating || !bulkCenter || !bulkService}
                onClick={addCenterServiceToSelectedUsers}
              >
                {bulkUpdating ? "Aplicando..." : "Agregar centro y servicio"}
              </button>
              <button type="button" className="crud-bulk-clear" onClick={clearBulkSelection}>Limpiar selección</button>
            </div>
          ) : null}

          <div className="crud-list-header">
            <input
              type="checkbox"
              checked={allVisibleUsersSelected}
              onChange={toggleVisibleUsers}
              aria-label="Seleccionar usuarios visibles"
            />
            <span>Nombre</span>
            <span>Email</span>
            <span>Permisos</span>
            <span>Acciones</span>
          </div>

          {usuariosPagina.length === 0 ? (
            <div className="crud-empty-state">
              No hay usuarios que coincidan con los filtros actuales.
            </div>
          ) : (
            <div className="crud-user-list">
              {usuariosPagina.map((usuario) => {
                const permissionSummary = getUserPermissionSummary(usuario);

                return (
                  <article key={usuario.username} className="crud-user-row">
                    <input
                      type="checkbox"
                      checked={selectedUsernames.includes(usuario.username)}
                      onChange={() => toggleUserSelection(usuario.username)}
                      aria-label={`Seleccionar ${usuario.username}`}
                    />
                    <div className="crud-user-main">
                      <strong>{usuario.username}</strong>
                      <span>{usuario.cargo}</span>
                    </div>

                    <div className="crud-user-email">
                      <a href={`mailto:${usuario.correo}`}>{usuario.correo}</a>
                    </div>

                    <div className="crud-user-permissions">
                      {permissionSummary.length === 0 ? (
                        <span className="crud-permission-chip muted">Sin permisos</span>
                      ) : (
                        permissionSummary.map((entry) => (
                          <div key={`${usuario.username}-${entry.region}`} className="crud-permission-group">
                            <span className="crud-permission-chip strong">{entry.regionLabel}</span>
                            <span className="crud-permission-text">{entry.detail}</span>
                          </div>
                        ))
                      )}
                    </div>

                    <div className="crud-user-actions">
                      <button
                        type="button"
                        className="crud-icon-button"
                        onClick={() => abrirModalEdicion(usuario)}
                        title="Editar usuario"
                      >
                        <FaEdit/>
                      </button>
                      <button
                        type="button"
                        className="crud-icon-button danger"
                        onClick={() => eliminarUsuario(usuario)}
                        title="Eliminar usuario"
                      >
                        <FaTrash/>
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}

          <div className="crud-list-footer">
            <span className="crud-pagination-summary" role="status">
              Mostrando {usuariosFiltrados.length === 0 ? 0 : indiceInicial + 1}
              {" a "}{Math.min(indiceInicial + usuariosPorPagina, usuariosFiltrados.length)}
              {" de "}{usuariosFiltrados.length} usuarios
            </span>
            {totalPaginas > 1 ? (
              <nav className="crud-pagination" aria-label="Páginas de usuarios">
                {Array.from({ length: totalPaginas }, (_, index) => (
                  <button
                    key={`page-${index + 1}`}
                    type="button"
                    className={`crud-page-button ${currentPage === index + 1 ? "is-active" : ""}`}
                    aria-label={`Página ${index + 1}`}
                    aria-current={currentPage === index + 1 ? "page" : undefined}
                    onClick={() => setCurrentPage(index + 1)}
                  >
                    {index + 1}
                  </button>
                ))}
              </nav>
            ) : null}
          </div>
        </div>
      </section>
      ) : null}

      {mostrarModal ? (
        <div className="crud-create-view">
          <div className={`crud-modal crud-create-panel ${modoEdicion ? "crud-edit-panel" : ""}`}>
            <div className="crud-modal-header">
              <div>
                <span className="crud-eyebrow">
                  {modoEdicion ? "Editar usuario" : "Crear usuarios"}
                </span>
                <h2>
                  {modoEdicion
                    ? `Editar ${nuevosUsuarios[0]?.username || "usuario"}`
                    : "Crear usuarios"}
                </h2>
                <p>
                  Activa los servicios necesarios y agrega <code>phone</code> solo si el
                  servicio debe enviar WhatsApp.
                </p>
              </div>
              {!modoEdicion && nuevosUsuarios.length > 1 ? (
                <span className="crud-pending-badge">
                  {nuevosUsuarios.length - 1} {nuevosUsuarios.length === 2 ? "usuario listo" : "usuarios listos"}
                </span>
              ) : null}
            </div>

            <div className="crud-modal-content">
              {nuevosUsuarios.map((draft, index) => (
                <section key={`draft-${index}`} className="crud-editor-card">
                  <div className="crud-editor-card-header">
                    <div>
                      <h3>{modoEdicion ? "Configuracion del usuario" : `Usuario ${index + 1}`}</h3>
                      <span>
                        {draft.cargo
                          ? `${draft.cargo} · ${draft.modo === "simple" ? "modo simple" : "modo detallado"}`
                          : "Completa los datos base y luego define permisos."}
                      </span>
                    </div>

                    {!modoEdicion ? (
                      <button
                        type="button"
                        className="crud-icon-button danger"
                        onClick={() => eliminarUsuarioFormulario(index)}
                        title="Quitar formulario"
                      >
                        <FaTrash />
                      </button>
                    ) : null}
                  </div>

                  <div className={`crud-form-grid ${modoEdicion ? "is-editing" : "is-creating"}`}>
                    {!modoEdicion ? (
                        <label className="crud-field">
                          <span>Username</span>
                          <input
                            type="text"
                            className={showCreateValidation && !draft.username.trim() ? "is-invalid" : ""}
                            value={draft.username}
                            onChange={(event) =>
                              handleChange(index, "username", event.target.value)
                            }
                            placeholder="jefe_planta"
                          />
                        </label>
                    ) : null}

                    <label className="crud-field">
                      <span>Correo</span>
                      <input
                        type="email"
                        className={showCreateValidation && !draft.correo.trim() ? "is-invalid" : ""}
                        value={draft.correo}
                        onChange={(event) =>
                          handleChange(index, "correo", event.target.value)
                        }
                        placeholder="correo@dominio.com"
                      />
                    </label>

                    <div className="crud-field crud-password-field">
                      <div className="crud-field-heading">
                        <span>{modoEdicion ? "Nueva contraseña" : "Contraseña"}</span>
                        <button type="button" className="crud-generate-password" onClick={() => generatePassword(index)}>
                          <FaRandom /> Generar
                        </button>
                      </div>
                      <div className="crud-password-control">
                          <input
                            type={visiblePasswords[index] ? "text" : "password"}
                            autoComplete="new-password"
                            maxLength={12}
                            className={showCreateValidation && ((!modoEdicion && !draft.password.trim()) || (draft.password && !isConfiguredPassword(draft.password))) ? "is-invalid" : ""}
                            value={draft.password}
                            onChange={(event) =>
                              handleChange(index, "password", event.target.value)
                            }
                            placeholder="12 caracteres"
                          />
                        <div className="crud-password-actions">
                          <button type="button" onClick={() => togglePasswordVisibility(index)} title={visiblePasswords[index] ? "Ocultar contraseña" : "Ver contraseña"}>
                            {visiblePasswords[index] ? <FaEyeSlash /> : <FaEye />}
                          </button>
                          <button type="button" onClick={() => copyPassword(draft.password)} disabled={!draft.password} title="Copiar contraseña">
                            <FaCopy />
                          </button>
                        </div>
                      </div>
                      <small>Mayúsculas · minúsculas · largo 12</small>
                    </div>

                    <div className="crud-field crud-password-field">
                      <span>Repetir contraseña</span>
                      <div className="crud-password-control">
                        <input
                          type={visiblePasswords[index] ? "text" : "password"}
                          autoComplete="new-password"
                          maxLength={12}
                          className={showCreateValidation && draft.password !== draft.confirmPassword ? "is-invalid" : ""}
                          value={draft.confirmPassword}
                          onChange={(event) => handleChange(index, "confirmPassword", event.target.value)}
                          placeholder="Repite la contraseña"
                        />
                        <div className="crud-password-actions">
                          <button type="button" onClick={() => togglePasswordVisibility(index)} title={visiblePasswords[index] ? "Ocultar contraseña" : "Ver contraseña"}>
                            {visiblePasswords[index] ? <FaEyeSlash /> : <FaEye />}
                          </button>
                        </div>
                      </div>
                      {showCreateValidation && draft.password !== draft.confirmPassword ? <small className="is-error">Las contraseñas no coinciden.</small> : null}
                    </div>

                    <div className={`crud-field crud-role-field ${showCreateValidation && !draft.cargo ? "is-invalid" : ""}`}>
                      <span>Rol</span>
                      {modoEdicion ? (
                        <select
                          value={draft.cargo}
                          onChange={(event) => handleChange(index, "cargo", event.target.value)}
                        >
                          <option value="">Selecciona un cargo</option>
                          {roles.map((role) => (
                            <option key={role.value} value={role.value}>{role.label}</option>
                          ))}
                        </select>
                      ) : (
                        <div className="crud-role-picker">
                          {roles.map((role) => (
                            <button
                              key={role.value}
                              type="button"
                              className={draft.cargo === role.value ? "is-selected" : ""}
                              onClick={() => handleChange(index, "cargo", role.value)}
                            >
                              {role.label}
                            </button>
                          ))}
                        </div>
                      )}
                      {showCreateValidation && !draft.cargo ? <small>Selecciona un cargo.</small> : null}
                    </div>
                  </div>

                  {["Jefe Region", "Jefe Area"].includes(draft.cargo) ? (
                    <div className="crud-mode-switch">
                      <span>Modo de asignacion</span>
                      <div>
                        <button
                          type="button"
                          className={`crud-mode-button ${
                            draft.modo === "simple" ? "is-active" : ""
                          }`}
                          onClick={() =>
                            updateDraftAt(index, (currentDraft) => ({
                              ...currentDraft,
                              modo: "simple",
                              centros: [],
                              serviciosPorCentro: {},
                              phonesByScope: Object.fromEntries(
                                Object.entries(currentDraft.phonesByScope || {}).filter(
                                  ([scopeKey]) => !scopeKey.startsWith("center:")
                                )
                              ),
                            }))
                          }
                        >
                          Simple
                        </button>
                        <button
                          type="button"
                          className={`crud-mode-button ${
                            draft.modo === "detallado" ? "is-active" : ""
                          }`}
                          onClick={() =>
                            updateDraftAt(index, (currentDraft) => ({
                              ...currentDraft,
                              modo: "detallado",
                            }))
                          }
                        >
                          Detallado
                        </button>
                      </div>
                    </div>
                  ) : null}

                  <div className="crud-permissions-builder">
                    <div className="crud-builder-header">
                      <h4>Permisos</h4>
                      <p>
                        El campo <code>phone</code> queda asociado dentro de cada servicio
                        activo. Si queda vacio, el sistema no enviara WhatsApp.
                      </p>
                    </div>

                    {!draft.cargo ? (
                      <div className="crud-permissions-empty">
                        Selecciona un cargo para comenzar a configurar permisos.
                      </div>
                    ) : (
                      <>
                      <div className="crud-shared-phone-panel">
                      <div className="crud-shared-phone-panel-copy">
                        <strong>Numeros compartidos</strong>
                        <span>
                          Agrega un numero una sola vez y luego seleccionarlo en varios
                          servicios, o escribe otro manual cuando lo necesites.
                        </span>
                      </div>

                      <div className="crud-shared-phone-actions">
                        <input
                          type="text"
                          inputMode="numeric"
                          maxLength={15}
                          placeholder="56912345678"
                          value={draft.phoneBookInput || ""}
                          onChange={(event) =>
                            handleChange(index, "phoneBookInput", event.target.value)
                          }
                        />
                        <button
                          type="button"
                          className="crud-secondary-button"
                          onClick={() => agregarTelefonoCompartido(index)}
                        >
                          Agregar numero
                        </button>
                      </div>

                      {(draft.phoneBook || []).length > 0 ? (
                        <div className="crud-shared-phone-list main">
                          {draft.phoneBook.map((savedPhone) => (
                            <span
                              key={`saved-${index}-${savedPhone}`}
                              className="crud-shared-phone-chip static"
                            >
                              {savedPhone}
                            </span>
                          ))}
                        </div>
                      ) : null}
                    </div>

                    <div className="crud-builder-body">
                      {renderPermissionBuilder(draft, index)}
                    </div>
                      </>
                    )}
                  </div>
                </section>
              ))}
            </div>

            <div className="crud-modal-footer">
              {!modoEdicion ? (
                <button
                  type="button"
                  className="crud-secondary-button"
                  onClick={agregarUsuarioCampo}
                >
                  + Añadir otro usuario
                </button>
              ) : (
                <span className="crud-footer-note">
                  Puedes dejar el phone vacio para mantener el servicio sin WhatsApp.
                </span>
              )}

              <div className="crud-footer-actions">
                {!modoEdicion && showCreateValidation ? (
                  <span className="crud-create-error">Completa los campos requeridos y configura los permisos antes de guardar.</span>
                ) : null}
                <button type="button" className="crud-ghost-button" onClick={limpiarModal}>
                  Volver a la lista
                </button>
                <button
                  type="button"
                  className="crud-primary-button"
                  onClick={modoEdicion ? guardarCambios : guardarUsuarios}
                >
                  {modoEdicion ? "Guardar edicion" : "Guardar"}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {confirmarEliminar ? (
        <div className="crud-modal-backdrop">
          <div className="crud-confirm-dialog">
            <h3>Eliminar usuario</h3>
            <p>
              Vas a eliminar a <strong>{usuarioAEliminar?.username}</strong>. Esta accion no
              se puede deshacer.
            </p>
            <div className="crud-footer-actions">
              <button
                type="button"
                className="crud-ghost-button"
                onClick={() => setConfirmarEliminar(false)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="crud-primary-button danger"
                onClick={confirmarEliminacion}
              >
                Si, eliminar
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {alertMessage ? (
        <Alert
          severity={alertSeverity}
          onClose={() => setAlertMessage(null)}
          className="crud-alert"
        >
          {alertMessage}
        </Alert>
      ) : null}

      {flashNotice ? (
        <div className="crud-flash-notice-backdrop">
          <div className={`crud-flash-notice ${flashNotice.type || "success"}`}>
            <strong>{flashNotice.message}</strong>
          </div>
        </div>
      ) : null}
    </div>
  );
}
